import type { EntityRiskValues } from '@/features/quotes/components/capital-global/types';
import { cleanCnpjOrCpf } from '@/shared/utils/document.util';
import type { ValidationError } from '@/shared/exceptions/validation.error';

export type RiskFieldKey =
  | 'sdQuantidadeDeVidas'
  | 'sdCapitalIndividual'
  | 'colQuantidadeDeVidas'
  | 'colCapitalIndividual'
  | 'uniQuantidadeDeVidas'
  | 'uniCapitalIndividual';

export type EntityRiskFieldErrors = Partial<Record<RiskFieldKey, string>>;

export type MapAttachValidationToRiskFieldErrorsResult = {
  /** Entity document id → field-level errors for the risk step form. */
  entityErrors: Record<string, EntityRiskFieldErrors>;
  /** Distinct entity labels affected (for toast summaries). */
  entityLabels: string[];
  /** Message when the error cannot be tied to a specific field. */
  generalMessage?: string;
  /** First entity tab to focus when validation fails. */
  firstInvalidEntityId?: string;
};

export type MapAttachValidationRiskContext = {
  entities: Array<{ id: string; label: string; sequenceNumber: number }>;
  /** Current form values per entity — used to resolve UNIFORME vs ESCALONADO field names. */
  riskDataMap?: Record<string, EntityRiskValues>;
};

type RiskGroup = 'partners' | 'employees' | 'unknown';
type RiskValueKind = 'lives' | 'capital' | 'unknown';

const INSUREMO_RISK_CAPITAL_MAX_CODE = 'ALBA-PA-Validation-BR014';
const INSUREMO_RISK_CAPITAL_MIN_CODE = 'ALBA-PA-Validation-BR015';
const INSUREMO_RISK_LIVES_MIN_CODE = 'ALBA-PA-Validation-BR016';
const INSUREMO_RISK_LIVES_MAX_CODE = 'ALBA-PA-Validation-BR017';

/**
 * Regra de SOMA da Api: não pertence a nenhum campo de nenhuma aba. Sem este
 * desvio o texto "vidas" faria o mapper ancorar o erro no campo de vidas da
 * primeira aba, culpando um estipulante arbitrário.
 */
const API_RISK_TOTAL_LIVES_CODE = 'ALBA-CG-RISK-TOTAL-LIVES';

/*
 * Limites do PRODUTO no InsureMO, usados só como pista para inferir o grupo de
 * uma mensagem BR014 que não identifica a faixa na chave.
 *
 * Eixo diferente dos tetos COMERCIAIS Alba (200k sócios / 100k colaboradores),
 * que vivem em `capital-global/steps/utils/step-helpers.util.ts` e no espelho da
 * Api. Não sincronize os dois: baixar o de colaboradores para 100k faria uma
 * BR014 que cita R$ 180 mil deixar de casar e o grupo virar `unknown`.
 *
 * Desde que a Api passou a barrar colaboradores acima de 100k antes do attach,
 * o ramo de 180k praticamente não é mais alcançado por employees.
 */
const ESCALONADO_PARTNERS_CAPITAL_PRODUCT_LIMIT = 200_000;
const ESCALONADO_EMPLOYEES_CAPITAL_PRODUCT_LIMIT = 180_000;

function parseNumericFieldValue(value: string): number {
  const normalized = value.trim();
  if (!normalized) return 0;

  const sanitized = normalized.replace(/[^\d,.-]/g, '');
  const numericText = sanitized.includes(',')
    ? sanitized.replace(/\./g, '').replace(',', '.')
    : sanitized;
  const parsed = parseFloat(numericText);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseBrlAmountFromMessage(message: string): number | undefined {
  const match = message.match(/R\$\s*([\d.\s]+,\d{2})/i);
  if (!match?.[1]) return undefined;

  const parsed = parseNumericFieldValue(match[1]);
  return parsed > 0 ? parsed : undefined;
}

function parseIntegerFromMessage(message: string, kind: 'min' | 'max'): number | undefined {
  const pattern =
    kind === 'min'
      ? /m[ií]nimo permitido de (\d+)/i
      : /m[aá]ximo permitido de (\d+)/i;
  const match = message.match(pattern);
  if (!match?.[1]) return undefined;

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function detectValueKindFromCode(code: string): RiskValueKind {
  if (
    code === INSUREMO_RISK_CAPITAL_MAX_CODE ||
    code === INSUREMO_RISK_CAPITAL_MIN_CODE
  ) {
    return 'capital';
  }

  if (
    code === INSUREMO_RISK_LIVES_MIN_CODE ||
    code === INSUREMO_RISK_LIVES_MAX_CODE
  ) {
    return 'lives';
  }

  return 'unknown';
}

function detectValueKindFromText(text: string): RiskValueKind {
  const normalized = text.toLowerCase();

  if (/n[uú]mero de vidas|quantidade de vidas|\bvidas\b/i.test(normalized)) {
    return 'lives';
  }

  if (/capital individual|\bcapital\b/i.test(normalized)) {
    return 'capital';
  }

  return 'unknown';
}

function detectGroupFromNumericFieldKey(fieldKey: string): RiskGroup {
  const trimmed = fieldKey.trim();
  if (!/^\d+$/.test(trimmed)) return 'unknown';

  const index = Number(trimmed);
  if (index === 0) return 'partners';
  if (index === 1) return 'employees';
  return 'unknown';
}

function detectGroupFromFieldKey(
  fieldKey: string,
  options?: { allowNumericIndex?: boolean }
): RiskGroup {
  if (options?.allowNumericIndex !== false) {
    const fromNumeric = detectGroupFromNumericFieldKey(fieldKey);
    if (fromNumeric !== 'unknown') return fromNumeric;
  }

  const normalized = fieldKey.toLowerCase();

  if (
    normalized.includes('partnersanddirectors') ||
    normalized.includes('partners_and_directors')
  ) {
    return 'partners';
  }

  if (normalized.includes('employees')) {
    return 'employees';
  }

  if (
    normalized.includes('membercategoryschedulelist[0]') ||
    /membercategory[^0-9]*1/.test(normalized)
  ) {
    return 'partners';
  }

  if (
    normalized.includes('membercategoryschedulelist[1]') ||
    /membercategory[^0-9]*2/.test(normalized)
  ) {
    return 'employees';
  }

  return 'unknown';
}

function detectGroupFromText(text: string): RiskGroup {
  const normalized = text.toLowerCase();

  if (/sócios?|socios?|diretores?|partners/i.test(normalized)) {
    return 'partners';
  }

  if (/colaboradores?|employees/i.test(normalized)) {
    return 'employees';
  }

  return 'unknown';
}

function isInsureMoRiskBusinessCode(code: string): boolean {
  return (
    code === INSUREMO_RISK_CAPITAL_MAX_CODE ||
    code === INSUREMO_RISK_CAPITAL_MIN_CODE ||
    code === INSUREMO_RISK_LIVES_MIN_CODE ||
    code === INSUREMO_RISK_LIVES_MAX_CODE
  );
}

function isInsureMoCapitalLimitCode(code: string): boolean {
  return (
    code === INSUREMO_RISK_CAPITAL_MAX_CODE ||
    code === INSUREMO_RISK_CAPITAL_MIN_CODE
  );
}

/** BR014: o limite citado na mensagem identifica o grupo — limites do produto no InsureMO (R$ 200k / R$ 180k), não os tetos comerciais. */
function inferGroupFromCapitalProductLimitMessage(
  message: string,
  code: string
): RiskGroup {
  if (code !== INSUREMO_RISK_CAPITAL_MAX_CODE) return 'unknown';

  const limit = parseBrlAmountFromMessage(message);
  if (limit === undefined) return 'unknown';

  if (limit >= ESCALONADO_PARTNERS_CAPITAL_PRODUCT_LIMIT) {
    return 'partners';
  }

  if (limit <= ESCALONADO_EMPLOYEES_CAPITAL_PRODUCT_LIMIT) {
    return 'employees';
  }

  return 'unknown';
}

function inferGroupFromFieldValues(
  message: string,
  valueKind: RiskValueKind,
  values: EntityRiskValues
): RiskGroup {
  if (values.perfilDoGrupo !== 'ESCALONADO') return 'unknown';

  if (valueKind === 'lives') {
    const min = parseIntegerFromMessage(message, 'min');
    const max = parseIntegerFromMessage(message, 'max');
    const sdLives = parseNumericFieldValue(values.sdQuantidadeDeVidas);
    const colLives = parseNumericFieldValue(values.colQuantidadeDeVidas);

    if (min !== undefined) {
      if (sdLives > 0 && sdLives < min) return 'partners';
      if (colLives > 0 && colLives < min) return 'employees';
    }

    if (max !== undefined) {
      if (sdLives > max) return 'partners';
      if (colLives > max) return 'employees';
    }
  }

  if (valueKind === 'capital') {
    const limit = parseBrlAmountFromMessage(message);
    if (limit === undefined) return 'unknown';

    const sdCapital = parseNumericFieldValue(values.sdCapitalIndividual);
    const colCapital = parseNumericFieldValue(values.colCapitalIndividual);
    const isAbove = /acima|maior/i.test(message);
    const isBelow = /abaixo|menor/i.test(message);

    if (isAbove) {
      if (sdCapital > limit) return 'partners';
      if (colCapital > limit) return 'employees';
    }

    if (isBelow) {
      if (sdCapital > 0 && sdCapital < limit) return 'partners';
      if (colCapital > 0 && colCapital < limit) return 'employees';
    }
  }

  return 'unknown';
}

function resolveEntityIdFromFieldKey(
  fieldKey: string,
  entities: MapAttachValidationRiskContext['entities']
): string | undefined {
  const seqMatch = fieldKey.match(/riskProfileList\[(\d+)\]/i);
  if (seqMatch) {
    const sequenceNumber = Number(seqMatch[1]);
    return entities.find(e => e.sequenceNumber === sequenceNumber)?.id;
  }

  if (/^\d+$/.test(fieldKey.trim())) {
    const index = Number(fieldKey);
    const sorted = [...entities].sort(
      (a, b) => a.sequenceNumber - b.sequenceNumber
    );
    return sorted[index]?.id ?? sorted[0]?.id;
  }

  // CNPJ pode ser alfanumérico (RFB): documento é `[A-Z0-9]{11,14}`.
  const docMatch = fieldKey.match(/documentNumber[^A-Z0-9]*([A-Z0-9]{11,14})/i);
  if (docMatch) {
    const document = cleanCnpjOrCpf(docMatch[1]);
    return entities.find(e => cleanCnpjOrCpf(e.id) === document)?.id;
  }

  return undefined;
}

function resolveFormFieldKey(
  group: RiskGroup,
  valueKind: RiskValueKind,
  perfilDoGrupo: EntityRiskValues['perfilDoGrupo']
): RiskFieldKey | undefined {
  if (valueKind === 'unknown') return undefined;

  if (perfilDoGrupo === 'UNIFORME') {
    return valueKind === 'lives'
      ? 'uniQuantidadeDeVidas'
      : 'uniCapitalIndividual';
  }

  if (group === 'partners') {
    return valueKind === 'lives'
      ? 'sdQuantidadeDeVidas'
      : 'sdCapitalIndividual';
  }

  if (group === 'employees') {
    return valueKind === 'lives'
      ? 'colQuantidadeDeVidas'
      : 'colCapitalIndividual';
  }

  return undefined;
}

function inferPerfilDoGrupo(
  entityId: string | undefined,
  riskDataMap: MapAttachValidationRiskContext['riskDataMap']
): EntityRiskValues['perfilDoGrupo'] {
  if (!entityId || !riskDataMap?.[entityId]) return 'ESCALONADO';
  return riskDataMap[entityId].perfilDoGrupo;
}

function registerFieldError(
  entityErrors: Record<string, EntityRiskFieldErrors>,
  entityLabels: Map<string, string>,
  entities: MapAttachValidationRiskContext['entities'],
  entityId: string | undefined,
  fieldKey: RiskFieldKey,
  message: string
) {
  if (!entityId) return;

  const existing = entityErrors[entityId] ?? {};
  if (!existing[fieldKey]) {
    entityErrors[entityId] = { ...existing, [fieldKey]: message };
  }

  const label = entities.find(e => e.id === entityId)?.label;
  if (label) entityLabels.set(entityId, label);
}

function mapSingleErrorEntry(
  fieldKey: string,
  code: string,
  message: string,
  context: MapAttachValidationRiskContext,
  entityErrors: Record<string, EntityRiskFieldErrors>,
  entityLabels: Map<string, string>,
  unmappedMessages: string[]
) {
  if (code === API_RISK_TOTAL_LIVES_CODE) {
    if (!unmappedMessages.includes(message)) unmappedMessages.push(message);
    return;
  }

  const entityId =
    resolveEntityIdFromFieldKey(fieldKey, context.entities) ??
    context.entities[0]?.id;

  const entityValues =
    (entityId && context.riskDataMap?.[entityId]) || undefined;
  const perfilDoGrupo = inferPerfilDoGrupo(entityId, context.riskDataMap);

  const valueKind =
    detectValueKindFromCode(code) !== 'unknown'
      ? detectValueKindFromCode(code)
      : detectValueKindFromText(message);

  const allowNumericIndex = !isInsureMoRiskBusinessCode(code);

  let group: RiskGroup = 'unknown';

  if (
    isInsureMoCapitalLimitCode(code) &&
    perfilDoGrupo === 'ESCALONADO'
  ) {
    group = inferGroupFromCapitalProductLimitMessage(message, code);
  }

  if (group === 'unknown') {
    group = detectGroupFromFieldKey(fieldKey, { allowNumericIndex });
  }
  if (group === 'unknown') {
    group = detectGroupFromText(message);
  }
  if (group === 'unknown' && entityValues) {
    group = inferGroupFromFieldValues(message, valueKind, entityValues);
  }
  if (group === 'unknown' && !allowNumericIndex) {
    group = detectGroupFromFieldKey(fieldKey, { allowNumericIndex: true });
  }

  const formFieldKey = resolveFormFieldKey(group, valueKind, perfilDoGrupo);

  if (formFieldKey && entityId) {
    registerFieldError(
      entityErrors,
      entityLabels,
      context.entities,
      entityId,
      formFieldKey,
      message
    );
    return;
  }

  if (!unmappedMessages.includes(message)) {
    unmappedMessages.push(message);
  }
}

/**
 * Maps InsureMO attach-element validation errors (HTTP 422) to risk step form fields.
 */
export function collectMappedFieldErrorMessages(
  entityErrors: Record<string, EntityRiskFieldErrors>
): string[] {
  return Object.values(entityErrors).flatMap(fields =>
    Object.values(fields).filter(Boolean)
  );
}

export function mapAttachValidationToRiskFieldErrors(
  error: ValidationError,
  context: MapAttachValidationRiskContext
): MapAttachValidationToRiskFieldErrorsResult {
  const entityErrors: Record<string, EntityRiskFieldErrors> = {};
  const entityLabels = new Map<string, string>();
  const unmappedMessages: string[] = [];

  for (const [fieldKey, fieldValue] of Object.entries(error.fieldErrors ?? {})) {
    const rawMessage = String(fieldValue?.message ?? '').trim();
    const code = String(fieldValue?.code ?? '').trim();
    if (!rawMessage) continue;

    mapSingleErrorEntry(
      fieldKey,
      code,
      rawMessage,
      context,
      entityErrors,
      entityLabels,
      unmappedMessages
    );
  }

  const firstInvalidEntityId = context.entities.find(
    entity =>
      entityErrors[entity.id] && Object.keys(entityErrors[entity.id]).length > 0
  )?.id;

  const generalMessage =
    unmappedMessages.length > 0 ? unmappedMessages.join('\n') : undefined;

  return {
    entityErrors,
    entityLabels: [...entityLabels.values()],
    generalMessage,
    firstInvalidEntityId,
  };
}

export function resolveVisibleRiskFieldError(
  formError: string | undefined,
  apiError: string | undefined,
  submitAttempted: boolean,
  options?: { alwaysVisibleError?: string }
): string | undefined {
  /*
   * Erro de REGRA — faixa de Capital Individual (5k–100k), soma de vidas acima
   * de 500 — aparece na hora: é consequência direta do que o corretor acabou de
   * digitar, e esconder até o submit faria ele descobrir tarde. Erro de campo
   * pela metade (obrigatoriedade) continua represado por `submitAttempted`.
   */
  if (options?.alwaysVisibleError) return options.alwaysVisibleError;

  if (!submitAttempted) return undefined;
  return apiError ?? formError;
}
